import { Injectable, Renderer2 } from '@angular/core';
import { ComponentsGroup } from '../../../models/componentGroup.model';
import { UtilsService } from '../utils/utils.service';
import { CustomerPage } from '../../../models/customerPage.model';
import { KeyValue, Location } from '@angular/common';
import { ComponentType } from '../../../shared/enums/component-type/component-type.module';
import { InputType } from '../../../shared/enums/input-type/input-type.module';
import { Schedule } from '../../../models/schedule.model';
import { ColorPicker } from '../../../models/colorPicker.model';
import { Page } from '../../../models/page.model';
import { PageType } from '../../../shared/enums/page-type/page-type.module';
import { FileData } from '../../../models/fileData.model';
import { ContentAddress } from '../../../models/contentAddress.model';
import { ContentIntitule } from '../../../models/contentIntitule.model';
import { ContentUrl } from '../../../models/contentUrl.model';
import { ContentActivityPrestations } from '../../../models/contentActivityPrestations.model';
import { Condition } from '../../../models/condition.model';
import { CustomerPageData } from '../../../models/customerPageData.model';
import { Component as ComponentModel } from '../../../models/component.model'
import { Rule } from '../../../models/rule.model';
import { OperatorType } from '../../../shared/enums/operator-type/operator-type.module';
import { FileStatus } from '../../../shared/enums/file-status/file-status.module';
import { OfferStatus } from '../../../shared/enums/offer-status/offer-status.module';
import { App } from '../../../shared/enums/app/app.module';
import { RoleType } from '../../../shared/enums/role-type/role-type.module';
import { Direction } from '../../../shared/enums/direction/direction.module';
import { EndpointType } from '../../../shared/enums/endpoint-type/endpoint-type.module';
import { ConnectionDefinition } from '../../../models/connectionDefinition.model';
import { CompletionStatus } from '../../../shared/enums/completion-status/completion-status.module';

@Injectable({
  providedIn: 'root'
})
export class CommonService {

  constructor(
    private utils: UtilsService,
    private location: Location
  ) { }

  staticComponentTypes = [ComponentType.BUTTON, ComponentType.HTML, ComponentType.TEXT, ComponentType.TITLE];

  buildNewGroup(order: number): ComponentsGroup{
    const groupId = this.utils.generateGUID();
    const newGroup : ComponentsGroup = {
      id: groupId,
      order: order,
      multipleConfig: {
        initQuantity: 1,
        minQuantity: 1,
        maxQuantity: 1,
        textBtnMultiple: "",
        haveIndexTitles: false
      },
      components: []
    }
    return newGroup;
  }

  fetchPageStats(customerPages: CustomerPage[], pageId: string): number | undefined {
    var page = customerPages.find(x => x.id === pageId);
    return page ? page.completionPercent : 0;
  }

  fetchPageStatus(customerPages: CustomerPage[], pageId: string): CompletionStatus | undefined {
    var page = customerPages.find(x => x.id === pageId);
    return page ? page.completionStatus : 0;
  }

  isCustomerPage(): boolean
  {
    const fullPath = this.location.path();
    // Split the URL to get the path segments, ignoring the query string
    const urlSegments = fullPath.split('?')[0].split('/');
    const firstPath = urlSegments.length > 1 ? urlSegments[1] : '';

    return firstPath === 'customer';
  }

  isAdminRoute(): boolean
  {
    const fullPath = this.location.path();
    // Split the URL to get the path segments, ignoring the query string
    const urlSegments = fullPath.split('?')[0].split('/');
    const firstPath = urlSegments.length > 1 ? urlSegments[1] : '';

    return firstPath === 'admin';
  }

  buildComponentDefaultValues(type: ComponentType, inputType: InputType | undefined = undefined, isMultiple: boolean = false): (string | Schedule | ColorPicker | FileData | ContentAddress | ContentIntitule | ContentUrl | ContentActivityPrestations)[]
  {
    var values: (string | Schedule | ColorPicker | FileData | ContentAddress | ContentIntitule | ContentUrl | ContentActivityPrestations)[] = [];
    if(type == ComponentType.SCHEDULE)
    {
      const defaultPeriod = [{ openingTime: "09:00", closingTime: "19:00" }];
      values = [{
        identifier: this.utils.generateGUID(),
        monday: { weekDay: "Monday",    periods: defaultPeriod },
        tuesday: { weekDay: "Tuesday",   periods: defaultPeriod },
        wednesday: { weekDay: "Wednesday", periods: defaultPeriod },
        thursday: { weekDay: "Thursday",  periods: defaultPeriod },
        friday: { weekDay: "Friday",    periods: defaultPeriod },
        saturday: { weekDay: "Saturday",  periods: [] },
        sunday: { weekDay: "Sunday",    periods: [] }
      }] as Schedule[];
    }
    else if(type == ComponentType.ADDRESS)
    {
      values = [{
        extendedWayLabel: "",
        localityLabel: "",
        postalCode: "",
        countryCode: "",
        number: undefined,
        complementLabel: "",
        trackType: "",
        laneLabel: ""
      }] as ContentAddress[];
    }
    else if(type == ComponentType.INTITULE)
    {
      values = [{
        identifier: this.utils.generateGUID(),
        denomination: "",
        denominationComplement: undefined
      }] as ContentIntitule[];
    }
    else if(type == ComponentType.URL)
    {
      values = [{
        identifier: this.utils.generateGUID(),
        label: "",
        type: ""
      }] as ContentUrl[];
    }
    else if(type == ComponentType.ACTIVITY_PRESTATIONS)
    {
      values = [{
        activity: {
          label: "",
          code: "",
          mainIndicator: false
        },
        prestations: []
      }] as ContentActivityPrestations[];
    }
    else if(type == ComponentType.INPUT && inputType != undefined && inputType == InputType.COLOR_PICKER)
    {
      values = [{ color: "#00000000", text: "" }] as ColorPicker[];
    }
    else if (isMultiple || (inputType != undefined && inputType == InputType.FILE) || type == ComponentType.VISUAL_PUBLICATION)
    {
      values = [];
    }
    else{
      values = [""];
    }

    return values;
  }

  orderPages(originalPages: Page[]): Page[] {
    var orderedPages = originalPages
        .map(page => ({
          ...page,
          order: page.order ?? 0
        }))
        .sort((a, b) => {
          const isACustomer = a.type === PageType.CUSTOMER;
          const isBCustomer = b.type === PageType.CUSTOMER;

          // Sort CUSTOMER pages first
          if (isACustomer && !isBCustomer) return -1;
          if (!isACustomer && isBCustomer) return 1;

          // If both are of the same type, sort by order
          const orderDiff = a.order - b.order;
          if (orderDiff !== 0) return orderDiff;

          // If order is the same, sort by title alphabetically
          return a.title?.localeCompare(b.title ?? '') ?? 0;
        });
    return orderedPages;
  }

  adjustAddComponentWidth(renderer: Renderer2): void {
    const container = document.querySelector('.block-list-item:not([hidden]) .drop-cards') as HTMLElement;
    const zoomContainer = document.querySelector('.block-list-item:not([hidden]) .drop-cards .zoom-container') as HTMLElement;
    if (container && zoomContainer) {
      const containerWidth = this.utils.getTotalWidth(container);
      const contentWidth = this.utils.getTotalWidth(zoomContainer);

      const scale = containerWidth / contentWidth;
      renderer.setStyle(zoomContainer, 'transform', `scale(${Math.min(scale, 1)})`);
    }
  }

  getConditionerValues(customersData: CustomerPageData[],
    sourceComponent: ComponentModel | undefined, sourceComponentGroup: ComponentsGroup, condition: Condition,
    targetComponentGroup: ComponentsGroup, targetDataGroup: KeyValue<string, CustomerPageData[]>, targetBlockIteration: number | undefined): string[] | ColorPicker[] | FileData[] | undefined {

    if (sourceComponent) {
      if (sourceComponentGroup.components.length === 1) { // source is single
        if (sourceComponent.type === ComponentType.INPUT && sourceComponent.config?.inputType === InputType.COLOR_PICKER) {
          return customersData.find(x => x.component.id === condition.componentId && x.blockIteration == targetBlockIteration)?.values as ColorPicker[];
        }
        else if (sourceComponent.type === ComponentType.INPUT && sourceComponent.config?.inputType === InputType.FILE) {
          return customersData.find(x => x.component.id === condition.componentId && x.blockIteration == targetBlockIteration)?.values as FileData[];
        }

        var values = customersData.find(x => x.component.id === condition.componentId && x.blockIteration == targetBlockIteration)?.values as string[];
        if(sourceComponent.type === ComponentType.CHECKBOX && values)
        {
          values = values.map(value => value === "" ? "0" : value);
        }
        return values;
      }

      if (sourceComponentGroup.id === targetComponentGroup.id) { // source is in the same group as component
        if (sourceComponent.type === ComponentType.INPUT && sourceComponent.config?.inputType === InputType.COLOR_PICKER) {
          return targetDataGroup.value.find(x => x.component.id === condition.componentId)?.values as ColorPicker[];
        }
        else if (sourceComponent.type === ComponentType.INPUT && sourceComponent.config?.inputType === InputType.FILE) {
          return targetDataGroup.value.find(x => x.component.id === condition.componentId)?.values as FileData[];
        }

        var values = targetDataGroup.value.find(x => x.component.id === condition.componentId)?.values as string[];
        if(sourceComponent.type === ComponentType.CHECKBOX && values)
        {
          values = values.map(value => value === "" ? "0" : value);
        }
        return values;
      }

      // source is in a different group
      if (sourceComponent.type === ComponentType.INPUT && sourceComponent.config?.inputType === InputType.COLOR_PICKER) {
        return customersData.filter(x => x.groupId === sourceComponentGroup.id && x.component.id === condition.componentId && x.blockIteration == targetBlockIteration).flatMap(x => x.values as ColorPicker[]);
      }
      else if (sourceComponent.type === ComponentType.INPUT && sourceComponent.config?.inputType === InputType.FILE) {
        return customersData.filter(x => x.groupId === sourceComponentGroup.id && x.component.id === condition.componentId && x.blockIteration == targetBlockIteration).flatMap(x => x.values as FileData[]);
      }

      var values = customersData.filter(x => x.groupId === sourceComponentGroup.id && x.component.id === condition.componentId && x.blockIteration == targetBlockIteration).flatMap(x => x.values as string[]);
      if(sourceComponent.type === ComponentType.CHECKBOX && values)
      {
        values = values.map(value => value === "" ? "0" : value);
      }
      return values;
    }
    else {
      return undefined;
    }

  }

  isConditionSatisfied(rules: Rule[], sourceComponent: ComponentModel, values: any[] | undefined): boolean {
    if (rules.length === 0) {
      return true;
    }

    if (!values || values.length === 0) return false;

    if (sourceComponent.type === ComponentType.INPUT &&
      (sourceComponent.config?.inputType === InputType.COLOR_PICKER || sourceComponent.config?.inputType === InputType.FILE)) {

      if (!rules.some(r => r.value === "*")) return false;

      if (sourceComponent.config?.inputType === InputType.COLOR_PICKER) {
        return (values as ColorPicker[]).some(v => !this.utils.hasAlphaZero(v.color) || v.text !== '');
      }

      if (sourceComponent.config?.inputType === InputType.FILE) {
        return (values as FileData[]).some(f => f.fileStatus === FileStatus.SUCCESS);
      }

      return false;
    }

    return this.isRulesSatisfied(rules, values as string[]);
  }

  // This method is also used on the frontend.
  // Any changes must be replicated there to keep behavior consistent.
  isRulesSatisfied(rules: Rule[], conditionerValues: string[]): boolean {
    if (!rules || rules.length === 0) return true;

    const groupedRules: Record<OperatorType, Rule[]> = rules.reduce(
      (acc, rule) => {
        const type: OperatorType = rule.operatorType ?? OperatorType.EQUAL; //when operatorType null, assumed as OperatorType.EQUAL
        if (!acc[type]) acc[type] = [];
        acc[type].push(rule);
        return acc;
      },
      {} as Record<OperatorType, Rule[]>
    );

    const equalRules = groupedRules[OperatorType.EQUAL];
    const hasEqualRules = !!equalRules;
    const equalSatisfied = hasEqualRules && equalRules.some(r => this.isRuleSatisfied(r.value, conditionerValues));
    if (equalSatisfied) return true;

    const regexRules = groupedRules[OperatorType.REGEX];
    const hasRegexRules = !!regexRules;
    const regexSatisfied = hasRegexRules && regexRules.some(r => {
      try {
          const regex = new RegExp(r.value);
          return conditionerValues.some(v => regex.test(v));
      } catch {
          return false; // invalid regex
      }
    });
    if (regexSatisfied) return true;

    const otherTypes = Object.keys(groupedRules).map(Number).filter(t => t !== OperatorType.EQUAL && t !== OperatorType.REGEX);
    if ((hasEqualRules || hasRegexRules) && otherTypes.length === 0) return false;

    for (const key of otherTypes) {
      const type = key as OperatorType;
      const group = groupedRules[type];
      if (type === OperatorType.EQUAL) continue;

      let isGroupValid = true;

      switch (type) {
        case OperatorType.NOT_EQUAL:
          isGroupValid = group.every(r => !this.isRuleSatisfied(r.value, conditionerValues));
          break;

        case OperatorType.GREATER_THAN:
          isGroupValid = group.some(r => {
            const ruleNum = parseFloat(r.value);
            if (!this.utils.isStringOnlyNumeric(r.value) || isNaN(ruleNum)) return false;

            return conditionerValues.some(v => {
              const val = parseFloat(v);
              return this.utils.isStringOnlyNumeric(v) && !isNaN(val) && val > ruleNum;
            });
          });
          break;

        case OperatorType.LESSER_THAN:
          isGroupValid = group.some(r => {
            const ruleNum = parseFloat(r.value);
            if (!this.utils.isStringOnlyNumeric(r.value) || isNaN(ruleNum)) return false;

            return conditionerValues.some(v => {
              const val = parseFloat(v);
              return this.utils.isStringOnlyNumeric(v) && !isNaN(val) && val < ruleNum;
            });
          });
          break;

        default:
          isGroupValid = true;
          break;
      }

      // If any group rules fail, then result fails (AND logic)
      if (!isGroupValid) return false;
    }

    return true;
  }

  isRuleSatisfied(ruleValue: string, values: string[]): boolean {
    if (ruleValue === "") {
      // Rule is empty, value must be empty
      return values.some(value => value === "");
    } else if (/^\*+$/.test(ruleValue)) {
      // Rule is one or more asterisks, value can be anything non-empty
      return values.some(value => value !== "");
    } else if (ruleValue.startsWith("*") && ruleValue.endsWith("*")) {
      // Rule is *word*, value must contain word
      const regex = new RegExp(ruleValue.slice(1, -1));
      return values.some(value => regex.test(value));
    } else if (ruleValue.startsWith("*")) {
      // Rule is *word, value must end with word
      const regex = new RegExp(ruleValue.slice(1) + "$");
      return values.some(value => regex.test(value));
    } else if (ruleValue.endsWith("*")) {
      // Rule is word*, value must start with word
      const regex = new RegExp("^" + ruleValue.slice(0, -1));
      return values.some(value => regex.test(value));
    } else {
      // Exact match
      return values.some(value => value === ruleValue);
    }
  }

  isComponentEditable(component: ComponentModel, offerStatus: OfferStatus, fullConnectionsDefinitions: ConnectionDefinition[], userRole: RoleType, isPreview: boolean): boolean{
    return userRole != RoleType.INTERN && !component.readOnly
    && !this.staticComponentTypes.includes(component.type!)
    && ((userRole === RoleType.ADMIN && component.roles?.some(x => x.accessibility.editable)) || (component.roles?.find(x => x.name === userRole)?.accessibility.editable ?? false))
    && (offerStatus == OfferStatus.OPEN || userRole === RoleType.ADMIN || isPreview)
    && this.hasConnectionAccessPostOrPatch(component, fullConnectionsDefinitions)
    && !component.connections?.some(c => c.connectionDefinition?.app == App.ICARD || c.connectionDefinition?.app == App.SOPROD);
    // && !component.connections?.some(c => c.direction == Direction.IN && c.trigger == TriggerType.DYNAMIC);
  }

  hasConnectionAccessPostOrPatch(component: ComponentModel, fullConnectionsDefinitions: ConnectionDefinition[])
  {
    var connectionsDefinitionsOut = component.connections?.filter(c => c.direction == Direction.OUT || c.direction == Direction.IN_OUT).flatMap(c => c.connectionDefinition);
    var connectionsDefinitionsOutIds = connectionsDefinitionsOut?.flatMap(cd => cd?.id);
    var connectionsDefinitionsOutTagsIds = connectionsDefinitionsOut?.flatMap(cd => cd?.values).flatMap(tag => tag?.id);
    var componentConnectionsDefinitions = fullConnectionsDefinitions
    .filter(cd => connectionsDefinitionsOutIds?.includes(cd.id))
    .map(cd => ({
      ...cd,
      values: cd.values ? [...cd.values] : []
    }));

    componentConnectionsDefinitions.forEach(cd => {
      cd.values = cd.values?.filter(tag => connectionsDefinitionsOutTagsIds?.includes(tag.id));
    });

    return componentConnectionsDefinitions.length == 0 || componentConnectionsDefinitions.some(cd =>
      cd.values?.some(tag =>
        tag.operations?.some(o =>
          (o.endpointType === EndpointType.POST || o.endpointType === EndpointType.PATCH) &&
          ((o.access && o.access?.length > 0) || cd.app != App.CONTENT)
        )
      )
    );
  }

  //#region Offer

  cleanCode(value: string): string {
    return this.utils.updateTextProperty(
      value,
      "",
      /^[a-zA-Z0-9_-]+$/,
      /[^A-Z0-9_-]/g
    );
  }

  handleInputCode(event: Event, listCodes: string[]): { cleaned: string, exists: boolean } {
    const input = event.target as HTMLInputElement;
    const code = input.value;
    const cleanedCode = this.cleanCode(code);

    if (code !== cleanedCode) {
      input.value = cleanedCode;
    }

    return {
      cleaned: cleanedCode,
      exists: listCodes.includes(cleanedCode)
    };
  }

  addCode(code: string, codesArray: string[], listCodes: string[], utils: any): boolean {
    let cleaned = this.cleanCode(code.trim());

    if (!cleaned || listCodes.includes(cleaned) || codesArray.includes(cleaned)) {
      return false;
    }

    codesArray.push(cleaned);
    listCodes.push(cleaned);

    return true;
  }

  removeCode(code: string, codesArray: string[], listCodes: string[]): boolean {
    const index = codesArray.indexOf(code);
    if (index >= 0) {
      codesArray.splice(index, 1);

      const listIndex = listCodes.indexOf(code);
      listCodes.splice(listIndex, 1);
      return true;
    }
    return false;
  }
  //#endregion

}
